import { useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'

import { getUserSetting } from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { QK, UserSettingName } from '@shared/utils'

import useFormatMessage from '@hooks/useFormatMessage'

import { getMissingInfo as getAboutMeMissingInfo } from './AboutMe/AboutMe.utils'
import { useInsurances } from './Insurance/Insurance.hooks'
import { getMissingInfo as getInsuranceMissingInfo } from './Insurance/Insurance.utils'
import { getMissingInfo as getProfileMissingInfo } from './Profile/Profile.utils'

export function usePatientUpdate() {
  const { id } = useMe()
  return useMutation({
    mutationFn: (data) => API.patients.id(id).update(data),
    onSuccess: (patient) =>
      setQueryData(QK.me(id).details, (prev = {}) => ({
        ...prev,
        ...patient,
        cbo: patient.cbo,
        pharmacy: patient.pharmacy,
      })),
  })
}

export function useProfileMissingErrors() {
  const me = useMe()
  const formatMessage = useFormatMessage()

  const { data: insurances, isPending } = useInsurances()

  const data = useMemo(() => {
    const data = {}

    const profileSectionMissingInfo = getProfileMissingInfo(me, formatMessage)
    data.profile = Object.keys(profileSectionMissingInfo).length

    const aboutSectionMissingInfo = getAboutMeMissingInfo(me, formatMessage)
    data.about = Object.keys(aboutSectionMissingInfo).length

    const insuranceSectionMissingInfo = getInsuranceMissingInfo(me, insurances?.length, formatMessage)
    data.insurance = Object.keys(insuranceSectionMissingInfo).length

    if (!me.height) {
      data.height = 1
    }
    if (!me.weight) {
      data.weight = 1
    }

    if (!me.language) {
      data.language = 1
    }

    if (!me.photoId) {
      data.photoId = 1
    }

    const contactMethodSetting = getUserSetting(me, UserSettingName.ContactMethod)
    if (!contactMethodSetting?.value) {
      data.contactMethod = 1
    }

    return data
    // Add photoId to track changes in packages/apps/patient/src/providers/MeProvider/MeProvider.js#L24
    // made to photoId dynamically added/loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatMessage, insurances?.length, me, me?.photoId])

  return { data, isPending }
}
